import type { Campaign } from '../../../api/fetchers/promotionsV2/types/campaign';
import { CampaignType } from '../../../api/fetchers/promotionsV2/types/campaign';
import type {
  PrizedropPrizeDetails,
  Promotion,
  TournamentPrizeDetails,
} from '../../../api/fetchers/promotionsV2/types/promotions';
import { PromoRoundType, PromotionType } from '../../../api/fetchers/promotionsV2/types/promotions';
import type { Trophy } from '../../../store/slices/promotionsV2/types';
import {
  CoBrandProms,
  Interval,
  PromoBackgroundModifiers,
  PromoNotificationType,
  SCREEN_TYPES,
  TimerStatusType,
} from '../types';

import { MediaBreakpoints } from './hooks/useMobile';

/* 
  Get promo background modifier
*/
export const getPromoBgColor = (brandName: string, promoType?: PromotionType): PromoBackgroundModifiers | string => {
  switch (brandName.toUpperCase()) {
    case CoBrandProms.DDW: {
      return PromoBackgroundModifiers.Blue;
    }
    case CoBrandProms.BronzeBlackJack:
    case CoBrandProms.SilverBlackJack:
    case CoBrandProms.GoldBlackJack:
    case CoBrandProms.PrivateBlackJack: {
      return brandName;
    }
    case CoBrandProms.NONDDW: {
      return PromoBackgroundModifiers.LightBlue;
    }
    default: {
      if (promoType && [PromotionType.Tournament, PromotionType.Prizedrop].includes(promoType)) {
        return PromoBackgroundModifiers.LightBlue;
      }
      if (promoType === PromotionType.BonusCard) {
        return PromoBackgroundModifiers.Brown;
      }
      return PromoBackgroundModifiers.Grey;
    }
  }
};

export const getPromoNotificationColor = (
  notificationType: PromoNotificationType
): PromoBackgroundModifiers | string => {
  switch (notificationType) {
    case PromoNotificationType.BonusCardLimitReached:
      return PromoBackgroundModifiers.Brown;
    case PromoNotificationType.BonusCardPromotionCancelled:
      return PromoBackgroundModifiers.Brown;
    default:
      return PromoBackgroundModifiers.Grey;
  }
};

export const getIsPromoStarted = (status: TimerStatusType): boolean => {
  return status !== TimerStatusType.STARTING;
};

export const getPrizes = (promotion: Promotion): TournamentPrizeDetails['prizes'] | PrizedropPrizeDetails['prizes'] => {
  switch (promotion.type) {
    case PromotionType.Tournament: {
      return (promotion.prizeDetails as unknown as TournamentPrizeDetails).prizes;
    }
    case PromotionType.Prizedrop: {
      return (promotion.prizeDetails as unknown as PrizedropPrizeDetails).prizes;
    }
    default: {
      return [];
    }
  }
};

export const scalePopUp = (wrapper: HTMLElement): void => {
  const modalContainer = wrapper.querySelector('div');
  if (!modalContainer) return;
  const scale = Math.max(
    1,
    modalContainer.clientWidth / wrapper.offsetWidth,
    modalContainer.clientHeight / wrapper.offsetHeight
  );
  modalContainer.style.transform = scale > 1 ? `scale(${1 / scale - 0.2})` : '';
  modalContainer.style.transition = 'transform .3s ease-in-out';
};

export const getRoundLimitMessage = (roundType: PromoRoundType | null): string => {
  switch (roundType) {
    case PromoRoundType.Daily:
      return 'DAILY_TOURNAMENT_LIMIT';
    case PromoRoundType.Total:
      return 'TOTAL_TOURNAMENT_LIMIT';
    default:
      return '';
  }
};

export const mapToNewTrophy = (oldTrophy: {
  minBet: string;
  showTrophyTooltip: boolean;
  isRoundLimitExceed: boolean;
  visibility: 'active' | 'inactive' | 'none';
  roundLimitExceededMessage: string;
}): Trophy => {
  return {
    enable: ['active', 'inactive'].includes(oldTrophy.visibility),
    isActive: oldTrophy.visibility === 'active',
    toolTipContent: oldTrophy.isRoundLimitExceed ? oldTrophy.roundLimitExceededMessage : 'PROMO_BET_BELOWMIN',
    minBet: oldTrophy.minBet,
  };
};

export const sortByEarlyExpiry = (promotions: Campaign[]): Campaign[] => {
  const sortedPromotionsByExpiryTime = [...promotions].sort((a, b) => {
    const e1 = a.campaignType === CampaignType.BonusPromo ? a.bonus.claimEndsIn : a.activeDuration.toEpoch;
    const e2 = b.campaignType === CampaignType.BonusPromo ? b.bonus.claimEndsIn : b.activeDuration.toEpoch;

    return e1 > e2 ? 1 : -1;
  });
  return sortedPromotionsByExpiryTime;
};

export function roundCurrency(value: number): number {
  if (value < 0.1) {
    return value;
  }
  if (value < 1) {
    return Math.floor(value * 10) / 10;
  }
  if (value < 10) {
    return Math.floor(value);
  }
  const factor = Math.pow(10, Math.floor(Math.log10(value)) - 1); // Define the rounding factor (e.g., 10, 100, 1000)
  return Math.floor(value / factor) * factor; // Round down based on the factor
}

export const SCREEN_NAME_FOR_LOGGED_IN_USER = 'YOU';

// Define allowed font-size ranges for each screen type
const fontSizeIntervals: { [key in SCREEN_TYPES]: Interval } = {
  [SCREEN_TYPES.DESKTOP]: { min: 23, max: 38 },
  [SCREEN_TYPES.PORTRAIT]: { min: 18, max: 22 },
  [SCREEN_TYPES.LANDSCAPE]: { min: 14, max: 20 },
};

// Character length range — used as basis to scale font size
const characterLimitInterval: Interval = { min: 9, max: 27 };

/**
 * Maps character length to an appropriate font size within a given font size interval.
 * Uses linear interpolation based on character length and clamps the result within min/max.
 *
 * @param charsLength - The number of characters in the title
 * @param fontSizeInterval - Font size bounds for the current screen type
 * @param charactersLimitInterval - Character length bounds to scale within
 * @returns A font size value (rounded and clamped)
 */
function getCharsLengthToFontSize(
  charsLength: number,
  fontSizeInterval: Interval,
  charactersLimitInterval: Interval
): number {
  // Linear interpolation: short titles → larger font, long titles → smaller font
  const val =
    ((charactersLimitInterval.max - charsLength) * (fontSizeInterval.max - fontSizeInterval.min)) /
      (charactersLimitInterval.max - charactersLimitInterval.min) +
    fontSizeInterval.min;

  // Clamp result between min and max font size
  return Math.min(Math.max(Math.round(val), fontSizeInterval.min), fontSizeInterval.max);
}

export function getTitleFontSize(mediaData: MediaBreakpoints, charsLength: number): number {
  const fontSizeInterval = mediaData.isPortrait
    ? fontSizeIntervals[SCREEN_TYPES.PORTRAIT]
    : mediaData.isLandscape
      ? fontSizeIntervals[SCREEN_TYPES.LANDSCAPE]
      : fontSizeIntervals[SCREEN_TYPES.DESKTOP];

  return getCharsLengthToFontSize(charsLength, fontSizeInterval, characterLimitInterval);
}
